import { z } from 'zod';
import { isIsoCurrency } from '../currencies.ts';
import {
  listCompareOperators,
  type ListColumn,
  type ListColumnComparison,
  type ListColumnType,
  type ListDefinition,
  type ListMoneyValue,
  type ListRelationFilter,
  type ListRelationTarget,
  type ListSort,
} from './contract.ts';

/**
 * Typisierte, versionierte Filter-Beschreibung des Listen-Baukastens (AD-32). Version 1: Filter (alle zusammen,
 * „und“) und Sortierung. Version 2 (Story 1.32, AD-32 Ergänzungen 04.10.2026): Bedingungsbaum mit Gruppen „und“/
 * „oder“ bis Tiefe 2 (höchstens 20 Bedingungen), „nicht“ je Bedingung, relative Datumsbedingungen, Spaltenvergleich
 * für angemeldete Paare und Schnellsuche. Version 1 wird beim Prüfen hochgestuft (`upgradeListQuery`); gespeicherte
 * Ansichten bleiben gültig. Kennt nur Spalten des Registers und die Operatoren ihres Typs; daraus baut `reporting`
 * parametrisiertes SQL. Rein (ohne Datenbank, ohne Uhr): „heute“ kommt als Eingabe.
 */

/**
 * Entfallene Spalte im Hinweis (EXPERIENCE §4.9 „Spalte entfallen“): sonst der Schlüssel („Spalte ‚catalog.cf_modell‘“);
 * ein Schlüssel mit technischer Kennung am Ende (z. B. gelöschte Eigenschaft `catalog.prop_<uuid>`, Story 5.91) sagt
 * niemandem etwas – dann nur „Diese Spalte“.
 */
export function missingColumnText(column: string): string {
  return /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(column) ? 'Diese Spalte' : `Spalte „${column}“`;
}

export const LIST_QUERY_VERSION = 1 as const;
export const LIST_QUERY_V2 = 2 as const;

/** Operatoren je Spaltentyp (EXPERIENCE §4.9 „Filter je Spaltentyp“). */
export const textOperators = ['contains', 'equals', 'starts_with', 'is_empty', 'is_not_empty'] as const;
export const numberOperators = ['eq', 'ne', 'lt', 'lte', 'gt', 'gte', 'between', 'is_empty'] as const;
export const dateOperators = ['today', 'last_7_days', 'last_30_days', 'this_month', 'last_month', 'between', 'before', 'after', 'is_empty'] as const;
export const choiceOperators = ['in', 'not_in'] as const;
/** Mehrfachwerte (Story 5.91: auch „ist leer“ / „ist nicht leer“; ein leeres Array zählt als leer). */
export const multiChoiceOperators = ['contains_any', 'contains_all', 'contains_none', 'is_empty', 'is_not_empty'] as const;
export const booleanOperators = ['is_true', 'is_false'] as const;

export const listFilterOperators = [
  ...new Set([...textOperators, ...numberOperators, ...dateOperators, ...choiceOperators, ...multiChoiceOperators, ...booleanOperators]),
] as readonly ListFilterOperator[];

export type ListFilterOperator =
  | (typeof textOperators)[number]
  | (typeof numberOperators)[number]
  | (typeof dateOperators)[number]
  | (typeof choiceOperators)[number]
  | (typeof multiChoiceOperators)[number]
  | (typeof booleanOperators)[number];

const operatorsByType: Record<ListColumnType, readonly ListFilterOperator[]> = {
  text: textOperators,
  number: numberOperators,
  money: numberOperators,
  quantity: numberOperators,
  percent: numberOperators,
  date: dateOperators,
  datetime: dateOperators,
  enum: choiceOperators,
  status: choiceOperators,
  multi_enum: multiChoiceOperators,
  boolean: booleanOperators,
  image: [],
  signals: [],
};

/** Version 2: relative Datumsbedingungen zum fachlichen Datum (Wert: ganze Zahl Tage). */
export const relativeDateOperators = ['older_than_days', 'within_next_days'] as const;
/** Version 2: Spalte gegen Spalte (nur angemeldete Paare, `ListColumn.comparisons`). */
export const COMPARE_COLUMN = 'compare_column' as const;

/** Operatoren einer Bedingung der Version 2 (Version 1 plus relative Daten und Spaltenvergleich). */
export type ListConditionOperator = ListFilterOperator | (typeof relativeDateOperators)[number] | typeof COMPARE_COLUMN;

export const listConditionOperators = [...listFilterOperators, ...relativeDateOperators, COMPARE_COLUMN] as readonly ListConditionOperator[];

/** Erlaubte Operatoren der Version 1 einer Spalte (leer = nicht filterbar). */
export function operatorsFor(type: ListColumnType): readonly ListFilterOperator[] {
  return operatorsByType[type];
}

/**
 * Erlaubte Operatoren der Version 2 einer Spalte: die der Version 1, bei Datum/Zeitpunkt zusätzlich „vor mehr als N
 * Tagen“ und „in den nächsten N Tagen“, und `compare_column`, wenn der Besitzer Vergleichspaare anmeldet. Text-Spalten
 * mit überschaubar vielen Werten (`distinctOptions`, z. B. Lagerplatz) bieten zusätzlich „ist eine von“ / „ist keine
 * von“ (Werteliste im Spaltenkopf, Story 1.32 Oberfläche).
 */
export function conditionOperatorsFor(column: {
  type: ListColumnType;
  comparisons?: readonly unknown[] | undefined;
  distinctOptions?: boolean | undefined;
}): readonly ListConditionOperator[] {
  const ops: ListConditionOperator[] = [...operatorsByType[column.type]];
  if (column.type === 'text' && column.distinctOptions) ops.push(...choiceOperators);
  if (column.type === 'date' || column.type === 'datetime') ops.push(...relativeDateOperators);
  if ((column.comparisons?.length ?? 0) > 0) ops.push(COMPARE_COLUMN);
  return ops;
}

/** Deutsche Wörter der Operatoren für die Auswahl im Filter (EXPERIENCE §4.9). */
export const operatorLabels: Record<ListConditionOperator, string> = {
  older_than_days: 'vor mehr als … Tagen',
  within_next_days: 'in den nächsten … Tagen',
  compare_column: 'verglichen mit Spalte',
  contains: 'enthält',
  equals: 'ist',
  starts_with: 'beginnt mit',
  is_empty: 'ist leer',
  is_not_empty: 'ist nicht leer',
  eq: '=',
  ne: '≠',
  lt: '<',
  lte: '≤',
  gt: '>',
  gte: '≥',
  between: 'zwischen',
  today: 'heute',
  last_7_days: 'letzte 7 Tage',
  last_30_days: 'letzte 30 Tage',
  this_month: 'dieser Monat',
  last_month: 'letzter Monat',
  before: 'vor',
  after: 'nach',
  in: 'ist eine von',
  not_in: 'ist keine von',
  contains_any: 'enthält eine von',
  contains_all: 'enthält alle',
  contains_none: 'enthält keine',
  is_true: 'ja',
  is_false: 'nein',
};

/** Welche Art Wert ein Operator braucht. */
export type OperatorValueKind = 'none' | 'scalar' | 'range' | 'list';

const NO_VALUE: readonly ListFilterOperator[] = ['is_empty', 'is_not_empty', 'today', 'last_7_days', 'last_30_days', 'this_month', 'last_month', 'is_true', 'is_false'];
const LIST_VALUE: readonly ListFilterOperator[] = ['in', 'not_in', 'contains_any', 'contains_all', 'contains_none'];

export function operatorValueKind(op: ListFilterOperator): OperatorValueKind {
  if (NO_VALUE.includes(op)) return 'none';
  if (LIST_VALUE.includes(op)) return 'list';
  if (op === 'between') return 'range';
  return 'scalar';
}

/** Art des Werts einer Bedingung der Version 2: zusätzlich Tage (relative Daten) und Vergleichsspalte. */
export function conditionValueKind(op: ListConditionOperator): OperatorValueKind | 'days' | 'compare' {
  if (op === 'older_than_days' || op === 'within_next_days') return 'days';
  if (op === COMPARE_COLUMN) return 'compare';
  return operatorValueKind(op);
}

export const LIST_TEXTS = {
  listUnknown: 'Diese Liste gibt es nicht.',
  queryVersion: 'Diese Filter-Beschreibung kennt das ERP nicht (Version 1 oder 2 erwartet). Bitte die Seite neu laden.',
  columnUnknown: (column: string) => `Spalte „${column}“ gibt es in dieser Liste nicht.`,
  columnDuplicate: (label: string) => `Die Spalte „${label}“ ist doppelt gewählt.`,
  columnNotFilterable: (label: string) => `Nach „${label}“ lässt sich nicht filtern.`,
  columnNotSortable: (label: string) => `Nach „${label}“ lässt sich nicht sortieren.`,
  operatorInvalid: (label: string) => `Diese Bedingung passt nicht zur Spalte „${label}“. Bitte eine Bedingung aus der Auswahl nehmen.`,
  valueMissing: (label: string) => `Bitte einen Wert für „${label}“ angeben.`,
  valueNotNeeded: (label: string) => `Diese Bedingung für „${label}“ braucht keinen Wert.`,
  valueInvalidText: (label: string) => `Bitte einen Text für „${label}“ eingeben (höchstens 200 Zeichen).`,
  valueInvalidNumber: (label: string) => `Bitte eine Zahl für „${label}“ eingeben.`,
  valueInvalidMoney: (label: string) => `Bitte einen Betrag und eine Währung für „${label}“ angeben; „von“ und „bis“ in derselben Währung.`,
  valueInvalidDate: (label: string) => `Bitte ein Datum für „${label}“ wählen.`,
  valueInvalidRange: (label: string) => `Bitte „von“ und „bis“ für „${label}“ angeben; „von“ darf nicht nach „bis“ liegen.`,
  valueInvalidChoice: (label: string) => `Bitte mindestens einen gültigen Wert für „${label}“ auswählen.`,
  tooManyFilters: 'Höchstens 20 Filter auf einmal.',
  groupTooDeep: 'Filtergruppen gehen höchstens zwei Ebenen tief.',
  groupOpInvalid: 'Eine Filtergruppe verknüpft mit „und“ oder „oder“.',
  itemUnknown: 'Diese Bedingung bzw. Gruppe kennt das ERP nicht. Bitte die Seite neu laden.',
  notInvalid: '„nicht“ ist ja oder nein.',
  valueUnknown: 'Diesen Wert kennt das ERP für keine Bedingung.',
  columnMissing: 'Bitte eine Spalte wählen.',
  valueInvalidDays: (label: string, max: number) =>
    `Bitte eine ganze Zahl Tage (0 bis ${new Intl.NumberFormat('de-DE').format(max)}; „in den nächsten“ ab 1) für „${label}“ angeben.`,
  compareNotOffered: (label: string) => `Diesen Vergleich bietet die Spalte „${label}“ nicht an.`,
  searchTooMany: (max: number) => (max === 1 ? 'Höchstens 1 Suchbegriff auf einmal.' : `Höchstens ${max} Suchbegriffe auf einmal.`),
  searchTooLong: 'Die Suche darf höchstens 200 Zeichen lang sein.',
  searchNotOffered: 'Diese Liste hat keine Schnellsuche.',
  columnsMissing: 'Bitte mindestens eine Spalte wählen.',
  suspendedColumn: (column: string) => `Filter ausgesetzt: ${missingColumnText(column)} gibt es nicht mehr.`,
  suspendedOperator: (label: string) => `Filter ausgesetzt: Die Bedingung passt nicht mehr zur Spalte „${label}“.`,
  suspendedDays: (label: string, max: number) =>
    `Filter ausgesetzt: „${label}“ nennt mehr Tage als erlaubt (höchstens ${new Intl.NumberFormat('de-DE').format(max)}, Einstellung „Listen“). Bitte den Filter anpassen.`,
  suspendedSearch: (max: number) =>
    `Schnellsuche ausgesetzt: höchstens ${max} ${max === 1 ? 'Begriff' : 'Begriffe'} (Einstellung „Listen“). Bitte Begriffe weglassen.`,
  sortSuspended: (column: string) => `Sortierung ausgesetzt: ${missingColumnText(column)} gibt es nicht mehr.`,
  viewNameMissing: 'Bitte einen Namen für die Ansicht angeben (höchstens 60 Zeichen).',
  relationUnknown: 'Diesen Beziehungsfilter gibt es in dieser Liste nicht.',
  relationTargetUnknown: (label: string) => `Diese Auswahl bietet der Filter „${label}“ nicht an.`,
  relationTargetValues: (label: string) => `Bitte mindestens einen Wert für „${label}“ wählen (höchstens 100).`,
  relationQuantityInvalid: (label: string) => `Bitte eine ganze Mindestmenge ab 1 für „${label}“ angeben.`,
  relationQuantityNotOffered: (label: string) => `Der Filter „${label}“ kennt keine Menge.`,
  relationPeriodInvalid: (label: string, max: number) =>
    `Bitte einen Zeitraum von 1 bis ${new Intl.NumberFormat('de-DE').format(max)} Tagen für „${label}“ angeben.`,
  suspendedRelation: (key: string) => `Filter ausgesetzt: Den Beziehungsfilter „${key}“ gibt es nicht mehr.`,
} as const;

const MAX_TEXT = 200;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Betrag eines Filters (Spaltentyp `money`): kleinste Einheit als Ziffernfolge mit Währung; verglichen wird nur in dieser Währung. */
export const listMoneyFilterValueSchema = z.object({ amountMinor: z.string().max(20), currency: z.string().max(3) });

/** Wert eines Filters: Text, Zahl, Betrag, Liste oder Bereich {from, to}. Grenzen nur gegen Missbrauch. */
export const listFilterValueSchema = z.union([
  z.string().max(MAX_TEXT),
  z.number(),
  listMoneyFilterValueSchema,
  z.array(z.union([z.string().max(MAX_TEXT), z.number()])).max(200),
  z.object({
    from: z.union([z.string().max(MAX_TEXT), z.number(), listMoneyFilterValueSchema]),
    to: z.union([z.string().max(MAX_TEXT), z.number(), listMoneyFilterValueSchema]),
  }),
]);
export type ListFilterValue = z.output<typeof listFilterValueSchema>;

export const listFilterSchema = z.object({
  column: z.string().min(1).max(120),
  op: z.enum(listFilterOperators as unknown as [ListFilterOperator, ...ListFilterOperator[]], {
    error: 'Diese Bedingung kennt das ERP nicht.',
  }),
  value: listFilterValueSchema.optional(),
});
export type ListFilter = z.output<typeof listFilterSchema>;

export const listSortSchema = z.object({
  column: z.string().min(1).max(120),
  direction: z.enum(['asc', 'desc']),
});

/** Filter-Beschreibung Version 1: `{ version: 1, filters: [{ column, op, value? }], sort: { column, direction } | null }`. */
export const listQuerySchema = z.object({
  version: z.literal(LIST_QUERY_VERSION, { error: LIST_TEXTS.queryVersion }),
  filters: z.array(listFilterSchema).max(20, LIST_TEXTS.tooManyFilters),
  sort: listSortSchema.nullable(),
});
export type ListQuery = z.output<typeof listQuerySchema>;

/** Leere Beschreibung (keine Filter, Sortierung der Liste). */
export function emptyListQuery(): ListQuery {
  return { version: LIST_QUERY_VERSION, filters: [], sort: null };
}

// ---------------------------------------------------------------------------
// Version 2

/** Höchstzahl Bedingungen über alle Gruppen (AD-32, Teil des Vertrags der Filter-Beschreibung). */
export const LIST_MAX_CONDITIONS = 20;
const MAX_CONDITIONS = LIST_MAX_CONDITIONS;

/**
 * Grenzen der Filter-Beschreibung Version 2 aus den versionierten Einstellungen (Gruppe „Listen“, `reporting`):
 * größte Zahl Tage relativer Datumsbedingungen und Höchstzahl Suchbegriffe. Kommen als Eingabe – nie als Konstante.
 */
export interface ListQueryLimits {
  maxRelativeDays: number;
  maxSearchTerms: number;
  /** Zeiträume der Kennzahlen in Tagen (Gruppe „Kennzahlen“), Pflicht bei Beziehungsfiltern mit Zeitraum (Story 1.33). */
  metricPeriods?: { short: number; medium: number; long: number };
}

/** Wert von `compare_column`: Vergleichsoperator und andere Spalte. */
export const listCompareValueSchema = z.object({
  op: z.enum(listCompareOperators, { error: 'Diesen Vergleich kennt das ERP nicht.' }),
  column: z.string({ error: LIST_TEXTS.columnMissing }).min(1, LIST_TEXTS.columnMissing).max(120, LIST_TEXTS.columnMissing),
});
export type ListCompareValue = z.output<typeof listCompareValueSchema>;

export const listConditionValueSchema = z.union([listFilterValueSchema, listCompareValueSchema], { error: LIST_TEXTS.valueUnknown });
export type ListConditionValue = z.output<typeof listConditionValueSchema>;

export const listConditionSchema = z.object({
  type: z.literal('condition', { error: LIST_TEXTS.itemUnknown }),
  column: z.string({ error: LIST_TEXTS.columnMissing }).min(1, LIST_TEXTS.columnMissing).max(120, LIST_TEXTS.columnMissing),
  op: z.enum(listConditionOperators as unknown as [ListConditionOperator, ...ListConditionOperator[]], {
    error: 'Diese Bedingung kennt das ERP nicht.',
  }),
  value: listConditionValueSchema.optional(),
  /** „nicht“: kehrt die Bedingung um; Zeilen ohne Wert erfüllen „nicht …“. */
  not: z.boolean({ error: LIST_TEXTS.notInvalid }).optional(),
});

/**
 * Zeitraum eines Beziehungsfilters (Story 1.33, AD-35): einer der Zeiträume aus den Einstellungen (Gruppe „Kennzahlen“:
 * kurz 30, mittel 90, lang 365 Tage), optional derselbe Zeitraum ein Jahr früher, oder „letzte N Tage“. Immer
 * Kalendertage zum fachlichen Datum, heute eingeschlossen (`periodWindow`).
 */
export const listRelationPeriodSchema = z.union(
  [
    z.object({ window: z.enum(['short', 'medium', 'long']), previousYear: z.boolean().optional() }).strict(),
    z.object({ days: z.number().int(), previousYear: z.boolean().optional() }).strict(),
  ],
  { error: 'Diesen Zeitraum kennt das ERP nicht.' },
);
export type ListRelationPeriod = z.output<typeof listRelationPeriodSchema>;

/**
 * Beziehungsbedingung (Story 1.33, AD-35): „hat …“ (`has: true`) bzw. „hat nie …“ über einen angemeldeten
 * `ListRelationFilter` der Liste; optional nur bestimmte Ziele (z. B. Artikel), Zeitraum und Mindestmenge (Summe im
 * Zeitraum).
 */
export const listRelationConditionSchema = z.object({
  type: z.literal('relation', { error: LIST_TEXTS.itemUnknown }),
  relation: z.string({ error: LIST_TEXTS.relationUnknown }).min(1, LIST_TEXTS.relationUnknown).max(120, LIST_TEXTS.relationUnknown),
  has: z.boolean({ error: LIST_TEXTS.relationUnknown }),
  target: z
    .object({ key: z.string().min(1).max(60), values: z.array(z.string().min(1).max(200)).min(1).max(100) })
    .optional(),
  period: listRelationPeriodSchema.optional(),
  minQuantity: z.number().optional(),
});

export interface ListRelationCondition {
  type: 'relation';
  relation: string;
  has: boolean;
  target?: { key: string; values: string[] } | undefined;
  period?: ListRelationPeriod | undefined;
  minQuantity?: number | undefined;
}

const groupOpSchema = z.enum(['and', 'or'], { error: LIST_TEXTS.groupOpInvalid });
const groupItemsError = { error: LIST_TEXTS.itemUnknown };
// Drei feste Ebenen statt Rekursion (kein beliebig tiefes JSON): Die dritte Ebene erkennt die Prüfung und meldet sie
// mit deutschem Satz (`groupTooDeep`); tiefer lehnt schon das Schema ab.
// Gruppe in der vierten Ebene: immer abgelehnt, mit deutschem Satz am Feld `type` (läuft nie bis zur Umwandlung).
const groupTooDeepSchema = z
  .object({ type: z.literal('group') })
  .refine(() => false, { message: LIST_TEXTS.groupTooDeep, path: ['type'] })
  .transform((): never => {
    throw new Error('Gruppe in vierter Ebene');
  });
const groupLevel3Schema = z.object({
  type: z.literal('group'),
  op: groupOpSchema,
  items: z
    .array(z.discriminatedUnion('type', [listConditionSchema, listRelationConditionSchema, groupTooDeepSchema], groupItemsError))
    .max(MAX_CONDITIONS, LIST_TEXTS.tooManyFilters),
});
const groupLevel2Schema = z.object({
  type: z.literal('group'),
  op: groupOpSchema,
  items: z
    .array(z.discriminatedUnion('type', [listConditionSchema, listRelationConditionSchema, groupLevel3Schema], groupItemsError))
    .max(MAX_CONDITIONS, LIST_TEXTS.tooManyFilters),
});
// Nach `type` unterschieden: Fehler kommen aus der passenden Form (deutscher Satz am Feld), nicht „Invalid input“.
export const listFilterGroupSchema = z.object({
  type: z.literal('group', { error: LIST_TEXTS.itemUnknown }),
  op: groupOpSchema,
  items: z
    .array(z.discriminatedUnion('type', [listConditionSchema, listRelationConditionSchema, groupLevel2Schema], groupItemsError))
    .max(MAX_CONDITIONS, LIST_TEXTS.tooManyFilters),
});

export interface ListCondition {
  type: 'condition';
  column: string;
  op: ListConditionOperator;
  value?: ListConditionValue | undefined;
  not?: boolean | undefined;
}

export interface ListFilterGroup {
  type: 'group';
  op: 'and' | 'or';
  items: (ListCondition | ListRelationCondition | ListFilterGroup)[];
}

/**
 * Filter-Beschreibung Version 2: `{ version: 2, where: Gruppe, sort, search? }`. Die Wurzel ist eine Gruppe; sie
 * enthält Bedingungen und Untergruppen (Tiefe 2), höchstens 20 Bedingungen insgesamt. `search`: Schnellsuche über
 * die vom Besitzer angemeldeten Suchfelder (alle Begriffe müssen passen).
 */
export const listQueryV2Schema = z.object({
  version: z.literal(LIST_QUERY_V2, { error: LIST_TEXTS.queryVersion }),
  where: listFilterGroupSchema,
  sort: listSortSchema.nullable(),
  search: z.string().max(MAX_TEXT, LIST_TEXTS.searchTooLong).optional(),
});

export interface ListQueryV2 {
  version: typeof LIST_QUERY_V2;
  where: ListFilterGroup;
  sort: ListSort | null;
  search?: string | undefined;
}

/** Version 1 oder 2 (Anfragen, gespeicherte Ansichten). */
export const anyListQuerySchema = z.discriminatedUnion('version', [listQuerySchema, listQueryV2Schema], { error: LIST_TEXTS.queryVersion });
export type AnyListQuery = ListQuery | ListQueryV2;

// Das Schema liefert eine Teilmenge des Baums (höchstens drei Ebenen).
const _schemaFitsType: (q: z.output<typeof listQueryV2Schema>) => ListQueryV2 = (q) => q;
void _schemaFitsType;

/** Version 1 → Version 2: alle Filter in einer „und“-Gruppe, Sortierung unverändert. */
export function upgradeListQuery(query: AnyListQuery): ListQueryV2 {
  if (query.version === LIST_QUERY_V2) return query;
  return {
    version: LIST_QUERY_V2,
    where: {
      type: 'group',
      op: 'and',
      items: query.filters.map((f) => ({ type: 'condition' as const, column: f.column, op: f.op, ...(f.value !== undefined ? { value: f.value } : {}) })),
    },
    sort: query.sort,
  };
}

/**
 * Feste Bedingung einer Seite (`scope`, z. B. Reiter „Ausgeblendet“ der Artikelliste) per „und“ vor die Filter einer
 * Abfrage; eine Quelle für Oberfläche (Zeilen, Kacheln, Export) und Server (Reiterzahlen der Ansichten, Story 5.100).
 */
export function withListScope(query: AnyListQuery, scope: ListCondition | ListFilterGroup | undefined | null): AnyListQuery {
  if (!scope) return query;
  const v2 = upgradeListQuery(query);
  const where: ListFilterGroup =
    v2.where.op === 'and' ? { ...v2.where, items: [scope, ...v2.where.items] } : { type: 'group', op: 'and', items: [scope, v2.where] };
  return { ...v2, where };
}

/** Schema der festen Bedingung (`scope`): eine Bedingung oder eine Gruppe. */
export const listScopeSchema = z.discriminatedUnion('type', [listConditionSchema, listFilterGroupSchema], { error: LIST_TEXTS.itemUnknown });

/** Alle Spalten, auf die eine Beschreibung Bezug nimmt (Bedingungen, Vergleichsspalten, Sortierung). */
export function queryColumnKeys(query: AnyListQuery): Set<string> {
  const out = new Set<string>();
  const walk = (group: ListFilterGroup) => {
    for (const item of group.items) {
      if (item.type === 'group') walk(item);
      else if (item.type === 'condition') {
        out.add(item.column);
        if (item.op === COMPARE_COLUMN && isCompareValue(item.value)) out.add(item.value.column);
      }
    }
  };
  walk(upgradeListQuery(query).where);
  if (query.sort) out.add(query.sort.column);
  return out;
}

/** Braucht die Beschreibung Grenzen aus den Einstellungen (relative Datumsbedingungen oder Schnellsuche)? */
export function queryNeedsLimits(query: AnyListQuery): boolean {
  if (query.version !== LIST_QUERY_V2) return false;
  if ((query.search ?? '').trim() !== '') return true;
  const walk = (group: ListFilterGroup): boolean =>
    group.items.some((item) =>
      item.type === 'group' ? walk(item) : item.type === 'relation' ? item.period !== undefined : (relativeDateOperators as readonly string[]).includes(item.op),
    );
  return walk(query.where);
}

function isCompareValue(v: unknown): v is ListCompareValue {
  return typeof v === 'object' && v !== null && !Array.isArray(v) && typeof (v as ListCompareValue).column === 'string' && typeof (v as ListCompareValue).op === 'string';
}

/** Grund, warum ein Filter oder die Sortierung ausgesetzt ist. */
export type SuspendReason = 'column_missing' | 'operator_invalid' | 'limit_exceeded' | 'relation_missing';

export interface SuspendedFilter {
  /** Laufende Nummer der Bedingung (Version 1: Index in `filters`), `-1` für die Sortierung. */
  index: number;
  /** Pfad in der Beschreibung, z. B. `filters.2`, `where.items.1.items.0` oder `sort`. */
  path: string;
  column: string;
  reason: SuspendReason;
  /** Satz für die graue, gestrichelte Pille. */
  text: string;
}

/** Normalisierter Filterwert; Beträge mit Währung. */
export type CheckedFilterValue = string | number | ListMoneyValue;

/** Eine geprüfte, aktive Bedingung: Spalte aus dem Register, Wert normalisiert; `not` kehrt sie um. */
export type CheckedFilter = { column: ListColumn; op: ListConditionOperator; not?: boolean } & (
  | { kind: 'none' }
  | { kind: 'scalar'; value: CheckedFilterValue }
  | { kind: 'range'; from: CheckedFilterValue; to: CheckedFilterValue }
  | { kind: 'list'; values: string[] }
  | { kind: 'days'; days: number }
  | { kind: 'compare'; comparison: ListColumnComparison; other: ListColumn }
);

/**
 * Geprüfte Gruppe. Ausgesetzte Bedingungen (Spalte entfallen) wirken nicht: Sie fehlen im Baum – in einer „und“-Gruppe
 * wie in einer „oder“-Gruppe (dort bleiben die übrigen Bedingungen als „oder“). Eine Gruppe ohne wirksame Bedingung
 * (leer oder alles ausgesetzt) fehlt ebenfalls und schränkt nicht ein – auch innerhalb einer „oder“-Gruppe; sie macht
 * die „oder“-Gruppe also nie „immer wahr“.
 */
export interface CheckedGroup {
  kind: 'group';
  op: 'and' | 'or';
  items: (CheckedFilter | CheckedRelation | CheckedGroup)[];
}

/**
 * Geprüfte Beziehungsbedingung (Story 1.33): Zeitraum als Zahl Tage zum fachlichen Datum (`periodWindow`), bei
 * `previousYear` ein Jahr früher; `null` = ohne Zeitraum. Mindestmenge = Summe der Menge im Zeitraum.
 */
export interface CheckedRelation {
  kind: 'relation';
  relation: ListRelationFilter;
  has: boolean;
  target: { target: ListRelationTarget; values: string[] } | null;
  period: { days: number; previousYear: boolean } | null;
  minQuantity: number | null;
}

/** Ist das Element eines geprüften Baums eine Gruppe? */
export function isCheckedGroup(item: CheckedFilter | CheckedRelation | CheckedGroup): item is CheckedGroup {
  return 'kind' in item && item.kind === 'group';
}

/** Ist das Element eines geprüften Baums eine Beziehungsbedingung? */
export function isCheckedRelation(item: CheckedFilter | CheckedRelation | CheckedGroup): item is CheckedRelation {
  return 'kind' in item && item.kind === 'relation';
}

export interface CheckedListQuery {
  /** Alle aktiven Bedingungen flach (in Reihenfolge), z. B. um die beteiligten Module zu verbinden. */
  filters: CheckedFilter[];
  /** Bedingungsbaum (Version 1: eine „und“-Gruppe). */
  where: CheckedGroup;
  /** Suchbegriffe der Schnellsuche (leer = keine Suche); jeder muss in einem Suchfeld stehen. */
  search: string[];
  /** Wirksame Sortierung (ausgesetzt oder ohne Wahl → Sortierung der Liste). */
  sort: { column: ListColumn; direction: 'asc' | 'desc' };
  suspended: SuspendedFilter[];
  /** Fehler je Feld (`filters.0.value` → Satz); leer = gültig. */
  errors: Record<string, string>;
  /**
   * Nur echte Daten (Story 8.5, D-07): Pflichtfilter jedes Exports – ohne Probe- und Beispielzeilen, sofern die Liste die
   * Spalten `run_mode`/`example` führt. Nie von außen wählbar.
   */
  realDataOnly?: boolean;
}

export interface CheckOptions {
  /**
   * Unbekannte Spalten in Filter oder Sortierung: `suspend` (gespeicherte Ansicht, Spalte entfallen – Filter wird
   * ausgesetzt und gemeldet, nie still entfernt) oder `reject` (Fehler je Feld).
   */
  unknownColumns: 'suspend' | 'reject';
  /** Unpassender Operator an einer vorhandenen Spalte: `reject` (Anfrage) oder `suspend` (gespeicherte Ansicht). */
  invalidOperators?: 'suspend' | 'reject';
  /**
   * Grenzen aus den Einstellungen; Pflicht, sobald die Beschreibung relative Datumsbedingungen oder eine Suche enthält
   * (sonst Programmfehler – ohne Einstellung gibt es keinen stillen Standardwert).
   */
  limits?: ListQueryLimits;
  /** Beziehungsfilter der Liste nach Schlüssel (Story 1.33). */
  relations?: ReadonlyMap<string, ListRelationFilter>;
}

function requireLimits(options: CheckOptions): ListQueryLimits {
  if (!options.limits) throw new Error('Grenzen der Filter-Beschreibung fehlen (Einstellungen der Gruppe „Listen“ lesen).');
  return options.limits;
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function isIsoDate(v: unknown): v is string {
  if (typeof v !== 'string' || !ISO_DATE.test(v)) return false;
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
}

/** Betrag in der kleinsten Einheit als Ziffernfolge (höchstens 19 Stellen, passt in bigint). */
const MONEY_MINOR = /^-?\d{1,19}$/;

type ValueCheck = { ok: true; value: CheckedFilterValue } | { ok: false; message: string };

function checkScalar(column: ListColumn, raw: unknown): ValueCheck {
  const { type, label } = column;
  if (type === 'text') {
    if (typeof raw !== 'string' || raw.trim() === '' || raw.length > MAX_TEXT) return { ok: false, message: LIST_TEXTS.valueInvalidText(label) };
    return { ok: true, value: raw.trim() };
  }
  if (type === 'date' || type === 'datetime') {
    if (!isIsoDate(raw)) return { ok: false, message: LIST_TEXTS.valueInvalidDate(label) };
    return { ok: true, value: raw };
  }
  // Beträge: kleinste Einheit der Währung exakt als Ziffernfolge, mit Währungscode (ISO 4217, AD-24).
  if (type === 'money') {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return { ok: false, message: LIST_TEXTS.valueInvalidMoney(label) };
    const { amountMinor, currency } = raw as Partial<ListMoneyValue>;
    if (typeof amountMinor !== 'string' || !MONEY_MINOR.test(amountMinor) || typeof currency !== 'string' || !isIsoCurrency(currency)) {
      return { ok: false, message: LIST_TEXTS.valueInvalidMoney(label) };
    }
    return { ok: true, value: { amountMinor: BigInt(amountMinor).toString(), currency } };
  }
  // Zahlenartig: Zahl.
  const n = typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : raw;
  if (!isFiniteNumber(n)) return { ok: false, message: LIST_TEXTS.valueInvalidNumber(label) };
  return { ok: true, value: n };
}

function compareValues(a: CheckedFilterValue, b: CheckedFilterValue): number | null {
  if (typeof a === 'object' || typeof b === 'object') {
    // Beträge: nur in derselben Währung vergleichbar.
    if (typeof a !== 'object' || typeof b !== 'object' || a.currency !== b.currency) return null;
    const x = BigInt(a.amountMinor);
    const y = BigInt(b.amountMinor);
    return x < y ? -1 : x > y ? 1 : 0;
  }
  return typeof a === 'number' && typeof b === 'number' ? a - b : String(a) < String(b) ? -1 : String(a) > String(b) ? 1 : 0;
}

/**
 * Prüft eine Filter-Beschreibung (Version 1 oder 2) gegen die Spalten einer Liste: nur Spalten des Registers, nur
 * filterbare bzw. sortierbare Spalten, nur die Operatoren ihres Typs, Werte passend zum Typ, Gruppen höchstens zwei
 * Ebenen tief, höchstens 20 Bedingungen. Ergebnis: Bedingungsbaum, flache Liste der aktiven Bedingungen, ausgesetzte
 * Bedingungen (Spalte entfallen) und Fehler je Feld. Fehlerpfade einer Version 1 bleiben `filters.N.…`.
 */
export function checkListQuery(
  definition: Pick<ListDefinition, 'defaultSort'>,
  columns: ReadonlyMap<string, ListColumn>,
  query: AnyListQuery,
  options: CheckOptions,
): CheckedListQuery {
  const filters: CheckedFilter[] = [];
  const suspended: SuspendedFilter[] = [];
  const errors: Record<string, string> = {};
  const invalidOperators = options.invalidOperators ?? 'reject';
  const fromV1 = query.version === LIST_QUERY_VERSION;
  const v2 = upgradeListQuery(query);
  let leaf = 0;

  const checkCondition = (condition: ListCondition, at: string): CheckedFilter | null => {
    const index = leaf++;
    const column = columns.get(condition.column);
    if (!column) {
      if (options.unknownColumns === 'suspend') {
        suspended.push({ index, path: at, column: condition.column, reason: 'column_missing', text: LIST_TEXTS.suspendedColumn(condition.column) });
      } else {
        errors[`${at}.column`] = LIST_TEXTS.columnUnknown(condition.column);
      }
      return null;
    }
    if (!column.filterable) {
      errors[`${at}.column`] = LIST_TEXTS.columnNotFilterable(column.label);
      return null;
    }
    // Spaltenvergleich, den der Besitzer nicht (mehr) anbietet: Angebot entfallen wie eine Spalte – bei gespeicherten
    // Beschreibungen (Abfrage und Lesen der Ansicht) ausgesetzt, nie 422 (sonst ließe sich die Ansicht nicht öffnen).
    const offerGone = invalidOperators === 'suspend' || options.unknownColumns === 'suspend';
    if (!conditionOperatorsFor(column).includes(condition.op)) {
      if (invalidOperators === 'suspend' || (condition.op === COMPARE_COLUMN && offerGone)) {
        suspended.push({ index, path: at, column: condition.column, reason: 'operator_invalid', text: LIST_TEXTS.suspendedOperator(column.label) });
      } else {
        errors[`${at}.op`] = LIST_TEXTS.operatorInvalid(column.label);
      }
      return null;
    }
    const base = { column, op: condition.op, ...(condition.not ? { not: true } : {}) };
    const kind = conditionValueKind(condition.op);
    const raw = condition.value;
    if (kind === 'none') {
      if (raw !== undefined && raw !== null && raw !== '') {
        errors[`${at}.value`] = LIST_TEXTS.valueNotNeeded(column.label);
        return null;
      }
      return { ...base, kind };
    }
    if (raw === undefined) {
      errors[`${at}.value`] = LIST_TEXTS.valueMissing(column.label);
      return null;
    }
    if (kind === 'days') {
      const min = condition.op === 'within_next_days' ? 1 : 0;
      const max = requireLimits(options).maxRelativeDays;
      if (typeof raw === 'number' && Number.isSafeInteger(raw) && raw > max && options.unknownColumns === 'suspend') {
        // Gespeicherte Ansicht bzw. Abfrage, deren Wert eine inzwischen kleiner gesetzte Grenze überschreitet: Hinweis
        // an der Ansicht statt Fehler (der Filter wirkt nicht, wird aber nie still entfernt).
        suspended.push({ index, path: at, column: condition.column, reason: 'limit_exceeded', text: LIST_TEXTS.suspendedDays(column.label, max) });
        return null;
      }
      if (typeof raw !== 'number' || !Number.isSafeInteger(raw) || raw < min || raw > max) {
        errors[`${at}.value`] = LIST_TEXTS.valueInvalidDays(column.label, max);
        return null;
      }
      return { ...base, kind, days: raw };
    }
    if (kind === 'compare') {
      const comparison = isCompareValue(raw) ? column.comparisons?.find((c) => c.op === raw.op && c.column === raw.column) : undefined;
      const other = comparison ? columns.get(comparison.column) : undefined;
      if (!comparison || !other) {
        // Gespeicherte Beschreibung, Paar nicht (mehr) angeboten: ausgesetzt und gemeldet wie ein unpassender Operator.
        if (offerGone) {
          suspended.push({ index, path: at, column: condition.column, reason: 'operator_invalid', text: LIST_TEXTS.suspendedOperator(column.label) });
        } else {
          errors[`${at}.value`] = LIST_TEXTS.compareNotOffered(column.label);
        }
        return null;
      }
      return { ...base, kind, comparison, other };
    }
    if (kind === 'scalar') {
      const checked = checkScalar(column, raw);
      if (!checked.ok) {
        errors[`${at}.value`] = checked.message;
        return null;
      }
      return { ...base, kind, value: checked.value };
    }
    if (kind === 'range') {
      if (typeof raw !== 'object' || raw === null || Array.isArray(raw) || !('from' in raw)) {
        errors[`${at}.value`] = LIST_TEXTS.valueInvalidRange(column.label);
        return null;
      }
      const from = checkScalar(column, raw.from);
      const to = checkScalar(column, raw.to);
      const order = from.ok && to.ok ? compareValues(from.value, to.value) : null;
      if (!from.ok || !to.ok || order === null || order > 0) {
        errors[`${at}.value`] = LIST_TEXTS.valueInvalidRange(column.label);
        return null;
      }
      return { ...base, kind, from: from.value, to: to.value };
    }
    // Liste (Auswahl, Status, Mehrfachwerte): bekannte Werte; mit `distinctOptions` auch vorhandene Werte der Liste.
    const values = Array.isArray(raw) ? [...new Set(raw.map(String))] : [];
    const known = column.options && !column.distinctOptions ? new Set(column.options.map((o) => o.value)) : null;
    if (values.length === 0 || values.some((v) => v.trim() === '' || v.length > MAX_TEXT || (known && !known.has(v)))) {
      errors[`${at}.value`] = LIST_TEXTS.valueInvalidChoice(column.label);
      return null;
    }
    return { ...base, kind: 'list', values };
  };

  const checkRelation = (condition: ListRelationCondition, at: string): CheckedRelation | null => {
    const index = leaf++;
    const relation = options.relations?.get(condition.relation);
    if (!relation) {
      if (options.unknownColumns === 'suspend') {
        suspended.push({ index, path: at, column: condition.relation, reason: 'relation_missing', text: LIST_TEXTS.suspendedRelation(condition.relation) });
      } else {
        errors[`${at}.relation`] = LIST_TEXTS.relationUnknown;
      }
      return null;
    }
    let target: CheckedRelation['target'] = null;
    if (condition.target) {
      const found = relation.targets.find((t) => t.key === condition.target!.key);
      if (!found) {
        errors[`${at}.target`] = LIST_TEXTS.relationTargetUnknown(relation.label);
        return null;
      }
      const values = [...new Set(condition.target.values.map((v) => v.trim()).filter((v) => v !== ''))];
      if (values.length === 0) {
        errors[`${at}.target`] = LIST_TEXTS.relationTargetValues(found.label);
        return null;
      }
      target = { target: found, values };
    }
    let period: CheckedRelation['period'] = null;
    if (condition.period) {
      const limits = requireLimits(options);
      let days: number;
      if ('window' in condition.period) {
        if (!limits.metricPeriods) throw new Error('Zeiträume der Kennzahlen fehlen (Einstellungen der Gruppe „Kennzahlen“ lesen).');
        days = limits.metricPeriods[condition.period.window];
      } else days = condition.period.days;
      if (!Number.isSafeInteger(days) || days < 1 || days > limits.maxRelativeDays) {
        if (options.unknownColumns === 'suspend' && Number.isSafeInteger(days) && days > limits.maxRelativeDays) {
          suspended.push({ index, path: at, column: condition.relation, reason: 'limit_exceeded', text: LIST_TEXTS.suspendedDays(relation.label, limits.maxRelativeDays) });
        } else {
          errors[`${at}.period`] = LIST_TEXTS.relationPeriodInvalid(relation.label, limits.maxRelativeDays);
        }
        return null;
      }
      period = { days, previousYear: condition.period.previousYear === true };
    }
    let minQuantity: number | null = null;
    if (condition.minQuantity !== undefined) {
      if (!relation.quantityField) {
        errors[`${at}.minQuantity`] = LIST_TEXTS.relationQuantityNotOffered(relation.label);
        return null;
      }
      if (!Number.isSafeInteger(condition.minQuantity) || condition.minQuantity < 1) {
        errors[`${at}.minQuantity`] = LIST_TEXTS.relationQuantityInvalid(relation.label);
        return null;
      }
      minQuantity = condition.minQuantity;
    }
    return { kind: 'relation', relation, has: condition.has, target, period, minQuantity };
  };

  let conditionCount = 0;
  const walk = (group: ListFilterGroup, path: string, depth: number): CheckedGroup => {
    const items: (CheckedFilter | CheckedRelation | CheckedGroup)[] = [];
    group.items.forEach((item, i) => {
      const at = depth === 1 && fromV1 ? `filters.${i}` : `${path}.items.${i}`;
      if (item.type === 'group') {
        if (depth >= 2) {
          errors[at] = LIST_TEXTS.groupTooDeep;
          return;
        }
        const inner = walk(item, at, depth + 1);
        if (inner.items.length > 0) items.push(inner);
        return;
      }
      conditionCount++;
      if (item.type === 'relation') {
        const checked = checkRelation(item, at);
        if (checked) items.push(checked);
        return;
      }
      const checked = checkCondition(item, at);
      if (checked) {
        items.push(checked);
        filters.push(checked);
      }
    });
    return { kind: 'group', op: group.op, items };
  };
  const where = walk(v2.where, 'where', 1);
  if (conditionCount > MAX_CONDITIONS) errors[fromV1 ? 'filters' : 'where'] = LIST_TEXTS.tooManyFilters;

  // Schnellsuche: Begriffe durch Leerraum getrennt, ohne Doppel; nur mit angemeldeten Suchfeldern.
  let search = [...new Set((v2.search ?? '').trim().split(/\s+/).filter((t) => t !== ''))];
  const maxTerms = search.length > 0 ? requireLimits(options).maxSearchTerms : 0;
  if (search.length > maxTerms && options.unknownColumns === 'suspend') {
    // Wie bei relativen Daten: Hinweis statt Fehler; die Suche wirkt dann nicht.
    suspended.push({ index: -1, path: 'search', column: '', reason: 'limit_exceeded', text: LIST_TEXTS.suspendedSearch(maxTerms) });
    search = [];
  } else if (search.length > maxTerms) errors.search = LIST_TEXTS.searchTooMany(maxTerms);
  else if (search.length > 0 && ![...columns.values()].some((c) => c.searchable)) errors.search = LIST_TEXTS.searchNotOffered;

  const fallback = columns.get(definition.defaultSort.column);
  if (!fallback) throw new Error(`Sortierung der Liste: Spalte ${definition.defaultSort.column} fehlt im Register.`);
  let sort = { column: fallback, direction: definition.defaultSort.direction };
  if (v2.sort) {
    const column = columns.get(v2.sort.column);
    if (!column) {
      if (options.unknownColumns === 'suspend') {
        suspended.push({ index: -1, path: 'sort', column: v2.sort.column, reason: 'column_missing', text: LIST_TEXTS.sortSuspended(v2.sort.column) });
      } else {
        errors['sort.column'] = LIST_TEXTS.columnUnknown(v2.sort.column);
      }
    } else if (!column.sortable) {
      errors['sort.column'] = LIST_TEXTS.columnNotSortable(column.label);
    } else {
      sort = { column, direction: v2.sort.direction };
    }
  }
  return { filters, where, search, sort, suspended, errors };
}

/** Wirksame Sortierung als Beschreibung (für Fuß und Spaltenkopf). */
export function effectiveSort(checked: CheckedListQuery): ListSort {
  return { column: checked.sort.column.key, direction: checked.sort.direction };
}

// ---------------------------------------------------------------------------
// Zeiträume (relativ ab „heute“, Europe/Berlin – kommt als Eingabe)

function shiftDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function monthStart(isoDate: string, monthsBack: number): string {
  const d = new Date(`${isoDate.slice(0, 7)}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() - monthsBack);
  return d.toISOString().slice(0, 10);
}

/**
 * Datumsbereich eines Datums-Filters als halb offenes Intervall `[from, toExclusive)` in fachlichen Daten
 * (`YYYY-MM-DD`). `null` an einer Seite = offen. `today` = fachliches Datum aus `ctx.clock` (Testuhr, Story 1.17).
 */
export function resolveDateRange(
  op: ListConditionOperator,
  today: string,
  value?: { from?: string | number; to?: string | number; at?: string | number; days?: number },
): { from: string | null; toExclusive: string | null } {
  switch (op) {
    // „vor mehr als N Tagen“: vor dem Tag heute − N (N = 90 → angelegt vor 91 oder mehr Tagen).
    case 'older_than_days':
      return { from: null, toExclusive: shiftDays(today, -Number(value?.days)) };
    // „in den nächsten N Tagen“: heute und die N − 1 Tage danach (wie „letzte N Tage“, AD-35).
    case 'within_next_days':
      return { from: today, toExclusive: shiftDays(today, Number(value?.days)) };
    case 'today':
      return { from: today, toExclusive: shiftDays(today, 1) };
    case 'last_7_days':
      return { from: shiftDays(today, -6), toExclusive: shiftDays(today, 1) };
    case 'last_30_days':
      return { from: shiftDays(today, -29), toExclusive: shiftDays(today, 1) };
    case 'this_month':
      return { from: monthStart(today, 0), toExclusive: monthStart(today, -1) };
    case 'last_month':
      return { from: monthStart(today, 1), toExclusive: monthStart(today, 0) };
    case 'between':
      return { from: String(value?.from), toExclusive: shiftDays(String(value?.to), 1) };
    case 'before':
      return { from: null, toExclusive: String(value?.at) };
    case 'after':
      return { from: shiftDays(String(value?.at), 1), toExclusive: null };
    default:
      throw new Error(`Kein Datumsbereich für ${op}`);
  }
}
